import OrderItemDetails from '../components/OrderItemDetails';
import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const formatOptionLabel = (key) => ({
  fit: 'Fit',
  size: 'Size',
  color: 'Color',
  garment_type: 'Garment',
  school_name: 'School',
}[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));

const itemOptionLines = (item) => {
  const opts = item.customization_options || {};
  const lines = [];
  ['fit', 'size', 'color', 'garment_type', 'school_name'].forEach((key) => {
    if (opts[key]) lines.push(`${formatOptionLabel(key)}: ${opts[key]}`);
  });
  return lines;
};

const bundleOptionGroups = (item) => {
  const selections = item.customization_options?.bundle_selections?.item_selections || [];
  return selections.map((sel, idx) => ({
    label: sel.product_name || `Bundle item ${idx + 1}`,
    details: [
      sel.fit ? `Fit: ${sel.fit}` : null,
      sel.size ? `Size: ${sel.size}` : null,
      sel.color ? `Color: ${sel.color}` : null,
    ].filter(Boolean),
  })).filter((group) => group.details.length > 0);
};

const CartPage = () => {
  const { cart, updateCartItem, removeFromCart, loading } = useCart();

  const tax = cart.subtotal * 0.06;
  const shipping = cart.subtotal > 75 ? 0 : 9.99;
  const total = cart.subtotal + tax + (cart.subtotal > 0 ? shipping : 0);

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="cart-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4 text-gray-900">Your cart is empty</h1>
            <p className="text-gray-500 mb-8">Looks like you haven't added any items yet.</p>
            <Link to="/shop"
              className="inline-flex items-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow">
              Continue Shopping <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      <section className="py-12 bg-[#4A90A4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-white">Shopping Cart</h1>
          <p className="text-white/70 mt-2">{cart.item_count} items in your cart</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div key={item.id} className="flex flex-col sm:flex-row gap-4 sm:gap-6 p-4 sm:p-6 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid={`cart-item-${item.id}`}>
                <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-xl overflow-hidden">
                  {item.product_image ? (
                    <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-gray-200">{item.product_name.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between">
                    <div>
                      <Link to={`/product/${item.product_slug}`} className="font-bold text-gray-900 hover:text-[#4A90A4] transition-colors">{item.product_name}</Link>
                      {item.variant_name && <p className="text-sm text-gray-500">{item.variant_name}</p>}
                      {!item.customization_options?.decoration && itemOptionLines(item).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {itemOptionLines(item).map((line) => (
                            <span key={line} className="inline-flex px-2 py-0.5 rounded-full bg-[#4A90A4]/10 text-[#1B3A5C] text-xs font-medium">
                              {line}
                            </span>
                          ))}
                        </div>
                      )}
                      {bundleOptionGroups(item).length > 0 && (
                        <div className="mt-2 space-y-1.5 rounded-lg bg-gray-50 p-2.5">
                          {bundleOptionGroups(item).map((group, idx) => (
                            <div key={`${group.label}-${idx}`} className="text-xs text-gray-600">
                              <span className="font-semibold text-gray-800">{group.label}:</span> {group.details.join(' · ')}
                            </div>
                          ))}
                        </div>
                      )}
                      {item.customization_options?.decoration && <OrderItemDetails item={item} />}
                      {item.pricing_error && <p role="alert" className="text-sm text-red-700">{item.pricing_error} <Link className="underline" to={`/product/${item.product_slug}`}>Configure this product again</Link>, then remove this old item.</p>}
                      {!item.customization_options?.decoration && item.customization_text && <p className="text-sm text-[#4A90A4] mt-1">Custom: {item.customization_text}</p>}
                    </div>
                    <p className="font-bold text-[#4A90A4]">${item.total_price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                      <button onClick={() => updateCartItem(item.id, item.quantity - 1)} disabled={loading}
                        className="p-2.5 hover:bg-gray-50 transition-colors" data-testid={`decrease-${item.id}`}>
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 py-2 min-w-[3rem] text-center font-medium">{item.quantity}</span>
                      <button onClick={() => updateCartItem(item.id, item.quantity + 1)} disabled={loading}
                        className="p-2.5 hover:bg-gray-50 transition-colors" data-testid={`increase-${item.id}`}>
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(item.id)} disabled={loading}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors" data-testid={`remove-${item.id}`}>
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="p-6 bg-[#FDF6E9] rounded-2xl sticky top-24 border border-[#F5A623]/15">
              <h2 className="text-xl font-bold mb-6 text-gray-900">Order Summary</h2>
              <div className="space-y-4 mb-6 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-medium">${cart.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tax (6%)</span><span className="font-medium">${tax.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span className="font-medium">{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span></div>
                {cart.subtotal < 75 && cart.subtotal > 0 && (
                  <p className="text-xs text-[#4A90A4]">Add ${(75 - cart.subtotal).toFixed(2)} more for free shipping!</p>
                )}
              </div>
              <div className="border-t border-[#F5A623]/20 pt-4 mb-6">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-[#F5A623]">${total.toFixed(2)}</span>
                </div>
              </div>
              <Link to={cart.items.some(i => i.pricing_error) ? "/cart" : "/checkout"} aria-disabled={cart.items.some(i => i.pricing_error)}
                className="w-full flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow"
                data-testid="checkout-btn"
              >
                {cart.items.some(i => i.pricing_error) ? 'Update the items above to continue' : 'Proceed to Checkout'} <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/shop" className="block text-center mt-4 text-gray-500 hover:text-[#4A90A4] transition-colors text-sm">Continue Shopping</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
